import { Pin, PinOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { usePreferences } from '../../api/auth';
import { AutosizeTextarea } from '../../components/ui/autosize-textarea';
import { IconButton } from '../../components/ui/icon-button';
import { ChecklistEditor } from './checklist-editor';
import type { NoteDraft } from './draft';

/** Champs d'édition communs au compositeur et à l'éditeur : épingle, titre, texte ou liste. */
export function NoteForm({
  draft,
  onChange,
  readOnly,
  autoFocus,
  header,
  compact,
}: {
  draft: NoteDraft;
  onChange: (patch: Partial<NoteDraft>) => void;
  readOnly?: boolean;
  autoFocus?: 'title' | 'content';
  header?: ReactNode;
  /** Compositeur : titre plus petit, comme dans Keep. */
  compact?: boolean;
}) {
  const { moveCheckedToBottom } = usePreferences();

  return (
    <div className="flex flex-col">
      {header}
      <div className="flex items-start gap-1 pt-3 pr-2 pl-4">
        <AutosizeTextarea
          value={draft.title}
          readOnly={readOnly}
          autoFocus={autoFocus === 'title'}
          onChange={(event) => onChange({ title: event.target.value.replace(/\r?\n/g, ' ') })}
          onKeyDown={(event) => {
            // Entrée dans le titre passe au contenu, comme dans Keep.
            if (event.key === 'Enter') {
              event.preventDefault();
              const form = event.currentTarget.closest('[data-note-form]');
              form?.querySelector<HTMLTextAreaElement>('[data-note-body] textarea')?.focus();
            }
          }}
          placeholder="Titre"
          aria-label="Titre"
          maxLength={1000}
          className={
            compact ? 'py-1.5 text-base leading-6 font-medium' : 'py-1 text-[22px] leading-7'
          }
        />
        {!readOnly && (
          <IconButton
            icon={draft.pinned ? PinOff : Pin}
            label={draft.pinned ? 'Retirer la note' : 'Épingler la note'}
            onClick={() => onChange({ pinned: !draft.pinned })}
            active={draft.pinned}
          />
        )}
      </div>
      <div data-note-body className="px-4 pt-1 pb-2">
        {draft.type === 'checklist' ? (
          <div className="-mx-3">
            {readOnly ? (
              <ul className="px-3 text-sm">
                {draft.items.map((item) => (
                  <li
                    key={item.id}
                    className={item.checked ? 'text-muted line-through' : undefined}
                  >
                    {item.checked ? '☑' : '☐'} {item.text}
                  </li>
                ))}
              </ul>
            ) : (
              <ChecklistEditor
                items={draft.items}
                onChange={(items) => onChange({ items })}
                moveCheckedToBottom={moveCheckedToBottom}
                autoFocus={autoFocus === 'content'}
              />
            )}
          </div>
        ) : (
          <AutosizeTextarea
            value={draft.content}
            readOnly={readOnly}
            autoFocus={autoFocus === 'content'}
            onChange={(event) => onChange({ content: event.target.value })}
            placeholder="Créer une note…"
            aria-label="Contenu de la note"
            maxLength={20000}
            className="min-h-[46px] text-base leading-6 tracking-[0.01em]"
          />
        )}
      </div>
    </div>
  );
}
